/**
 * Helpers for porting `src/scenes/*` React/Remotion components to
 * Hyperframes HTML+GSAP scene builders. Every ported scene depends on a
 * small set of patterns; this module centralizes them so each scene file
 * stays small and parity-checkable against the Remotion original.
 *
 * Conventions:
 *  - All `*Sec` arguments are **clip-local** seconds (0 = scene start). The
 *    scene builder converts them to absolute composition time by adding
 *    `clipStartSec` from {@link SceneBuildArgs}.
 *  - Element ids are prefixed with `clipPrefix` to namespace per-clip
 *    instances (e.g. `${clipPrefix}-title`).
 *  - Use `colors`/`tokens`/`withAlpha` from the existing brand modules so
 *    color tokens stay in sync with the Remotion source.
 */
import { tl, fadeIn, fadeOut } from "../timeline.js";
import { colors, brandName, brandTagline, brandUrl, ci, activeColorTheme } from "../../brand/theme.js";
import {
  withAlpha,
  weights,
  typeScale,
  type TypeStep,
} from "../../brand/tokens.js";
import { escapeHtml } from "../composition.js";
import type { FormatId, Fragment } from "../types.js";
import type { SceneInputs } from "../../scenes/types.js";

export { colors, withAlpha, weights, brandName, brandTagline, brandUrl, ci, escapeHtml };

/** Mirrors `useType(step)` — but pure / format-aware. */
export function typeSize(step: TypeStep, format: FormatId): number {
  return typeScale[step][format];
}

export function accentFor(inputs: SceneInputs): string {
  if (inputs.accent === "secondary") return colors.secondary;
  if (inputs.accent === "accent") return colors.accent;
  return colors.primary;
}

/**
 * Composes a tween line so its absolute-time `atSec` is computed by adding
 * the clip's `clipStartSec`. This lets every scene file think in
 * clip-local seconds while the project-wide timeline still has correct
 * positions.
 */
export function makeTl(clipStartSec: number) {
  return {
    set: (target: string, props: Record<string, unknown>, atSec: number) =>
      tl.set(target, props, clipStartSec + atSec),
    to: (target: string, props: Record<string, unknown>, atSec: number) =>
      tl.to(target, props, clipStartSec + atSec),
    from: (target: string, props: Record<string, unknown>, atSec: number) =>
      tl.from(target, props, clipStartSec + atSec),
    fromTo: (
      target: string,
      fromProps: Record<string, unknown>,
      toProps: Record<string, unknown>,
      atSec: number,
    ) => tl.fromTo(target, fromProps, toProps, clipStartSec + atSec),
    fadeIn: (target: string, atSec: number, dur = 0.4) => fadeIn(target, clipStartSec + atSec, dur),
    fadeOut: (target: string, atSec: number, dur = 0.4) => fadeOut(target, clipStartSec + atSec, dur),
  };
}

/**
 * Emits a global "outro fade" — common to many scenes (last 10 frames of
 * the scene fade the whole wrapper to 0). Use on the clip wrapper id.
 */
export function clipOutroFade(
  clipPrefix: string,
  clipDurationSec: number,
  clipStartSec: number,
  framesFromEnd = 10,
  fps = 30,
): string {
  const fadeStart = clipStartSec + clipDurationSec - framesFromEnd / fps;
  return tl.to(`#${clipPrefix}-wrap`, { opacity: 0, duration: framesFromEnd / fps, ease: "power1.in" }, fadeStart);
}

/* ============================================================
 * Convenience helpers for scene ports — these capture the
 * most common motion patterns so per-scene files stay under
 * ~80 LOC.
 * ============================================================ */

/**
 * Scene-baked SFX beat: ein einzelnes `<audio>`-Schnipsel, das beim Aufbau
 * der Szene auf die Animationsmarker getimet wird. Wird vom Hyperframes-
 * Runtime als ganz normales `<audio class="clip">` mit `data-start` /
 * `data-duration` adoptiert (siehe `app/src/hyperframesPlayer.tsx`).
 *
 *   sound  – Asset-Name aus `public/sounds/<name>.wav`
 *            (gültige IDs: tap, tick, click, pop, glass, shimmer, ding,
 *            notify, magic, swoosh, woosh, impact, thud, sub, riser,
 *            transition, sfx495, sfx503, sfx505, sfx676).
 *   atSec  – Clip-lokale Sekunde (0 = Scene-Start). Wird intern auf
 *            absolute Reel-Zeit umgerechnet.
 *   volume – 0..1. Für „dezent" Werte zwischen 0.12 und 0.35.
 *   durSec – Optional. Default 1.0s — die Datei selbst klingt aus.
 */
export type SceneSfxBeat = {
  sound:
    | "tap" | "tick" | "click" | "pop" | "glass" | "shimmer" | "ding"
    | "notify" | "magic" | "swoosh" | "woosh" | "impact" | "thud"
    | "sub" | "riser" | "transition"
    | "sfx495" | "sfx503" | "sfx505" | "sfx676";
  atSec: number;
  volume?: number;
  durSec?: number;
};

/**
 * Emit per-scene baked SFX as `<audio class="clip">` tags. Honors the
 * existing per-scene mute-flag `inputs.extras.sfx === false` (Inspector /
 * Layers-Panel toggle): wenn explizit `false`, wird gar nichts emittiert.
 *
 * Die Audio-URLs folgen der bereits etablierten Producer-Konvention
 * `assets/sounds/<name>.wav`, die in der Live-UI über `rewriteAssetUrls
 * ForPreview` automatisch auf `<origin>/sounds/<name>.wav` umgebogen wird.
 */
export function sceneSfxAudio(opts: {
  clipPrefix: string;
  clipStartSec: number;
  inputs: SceneInputs;
  beats: SceneSfxBeat[];
}): string {
  if (opts.inputs.extras?.sfx === false) return "";
  if (!opts.beats.length) return "";
  return opts.beats
    .map((b, i) => {
      const start = (opts.clipStartSec + Math.max(0, b.atSec)).toFixed(4);
      const dur = (b.durSec ?? 1.0).toFixed(4);
      const vol = Math.max(0, Math.min(1, b.volume ?? 0.22)).toFixed(3);
      const id = `${opts.clipPrefix}-sfx-${i}`;
      // Track-Index 80+ liegt weit über den Reel-Level-Audio-Slots (20+i)
      // und Overlay-Slots (1-19), damit der Linter im Producer keine
      // duplicate_audio_track-Warnung wirft.
      const trackIdx = 80 + i;
      return `<audio id="${id}" class="clip" src="assets/sounds/${b.sound}.wav" data-start="${start}" data-duration="${dur}" data-volume="${vol}" data-audio-kind="sfx" data-track-index="${trackIdx}" preload="auto" playsinline></audio>`;
    })
    .join("\n");
}

const SUPPORTED_IMG_EXT = [".png", ".jpg", ".jpeg", ".webp", ".svg"];

/** Resolve the CI brand logo to an `assets/brand/<file>` URL (staged by the producer). */
export function resolveBrandLogoSrc(): string | null {
  const logoPath = ci.logo ?? "src/brand/logo.svg";
  const ext = logoPath.toLowerCase().slice(logoPath.lastIndexOf("."));
  if (!SUPPORTED_IMG_EXT.includes(ext)) return null;
  const filename = logoPath.split("/").pop() ?? "logo.svg";
  return `assets/brand/${filename}`;
}

/**
 * Resolve a single media URL the way Remotion's `pickMedia` does, but for the
 * Hyperframes runtime where there is no `staticFile()`. Returns a value
 * suitable for an `<img src>` / `<video src>` inside the rendered page.
 */
export function pickMediaSrc(
  media: string | string[] | undefined | null,
  index = 0,
): string | undefined {
  let raw: string | undefined;
  if (Array.isArray(media)) raw = media[index];
  else if (typeof media === "string" && media.length > 0) raw = index === 0 ? media : undefined;
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;
  const staticMatch = trimmed.match(/^\/static\/(.+)$/);
  if (staticMatch) return `assets/${staticMatch[1]}`;
  const uploadsMatch = trimmed.match(/^\/uploads\/(.+)$/);
  if (uploadsMatch) return `assets/uploads/${uploadsMatch[1]}`;
  if (trimmed.startsWith("/")) return trimmed.slice(1);
  return `assets/${trimmed}`;
}

export function isVideoMedia(url: string | undefined | null): boolean {
  if (!url) return false;
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Emits an absolute-fill content wrapper for a scene clip. The wrapper id
 * is `${clipPrefix}-wrap` and the inner content node id is `${clipPrefix}-pad`.
 * Use this as the scaffolding for any scene that wants a centered/padded
 * column layout under the backdrop.
 */
export function clipFrame(opts: {
  clipPrefix: string;
  clipStartSec: number;
  clipDurationSec: number;
  paddingPx?: number;
  inner: string;
  flexDirection?: "row" | "column";
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "space-between";
  gapPx?: number;
  extraStyle?: string;
}): string {
  const align = opts.align ?? "center";
  const justify = opts.justify ?? "center";
  const dir = opts.flexDirection ?? "column";
  const gap = opts.gapPx ?? 0;
  const pad = opts.paddingPx ?? 96;
  const alignMap: Record<string, string> = { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" };
  const justifyMap: Record<string, string> = { start: "flex-start", center: "center", end: "flex-end", "space-between": "space-between" };
  return `<div id="${opts.clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:${dir};align-items:${alignMap[align]};justify-content:${justifyMap[justify]};gap:${gap}px;padding:${pad}px;${opts.extraStyle ?? ""}">
${opts.inner}
</div>`;
}

/** Standard title element + slide-up fade-in. Returns the fragment pieces. */
export function titleBlock(opts: {
  clipPrefix: string;
  text: string;
  clipStartSec: number;
  format: FormatId;
  step?: TypeStep;
  startAtSec?: number;
  color?: string;
  align?: "left" | "center" | "right";
  weight?: number;
  letterSpacing?: number;
  maxWidthPx?: number;
}): { html: string; init: string; tween: string; id: string } {
  const id = `${opts.clipPrefix}-title`;
  const size = typeSize(opts.step ?? "lg", opts.format);
  const align = opts.align ?? "center";
  const startAt = opts.startAtSec ?? 0;
  const t = makeTl(opts.clipStartSec);
  const ls = opts.letterSpacing ?? -2;
  const w = opts.weight ?? weights.black;
  const maxW = opts.maxWidthPx ? `max-width:${opts.maxWidthPx}px;` : "";
  const html = `<div id="${id}" style="font-family:'Outfit',sans-serif;font-weight:${w};font-size:${size}px;letter-spacing:${ls}px;color:${opts.color ?? colors.fg};line-height:1.05;text-align:${align};text-wrap:balance;${maxW}">${escapeHtml(opts.text)}</div>`;
  return {
    html,
    id,
    init: `gsap.set("#${id}", { opacity: 0, y: 16 });`,
    tween: t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, startAt),
  };
}

/** Subtext / body line — fades in with a small delay. */
export function bodyBlock(opts: {
  clipPrefix: string;
  text: string;
  clipStartSec: number;
  format: FormatId;
  step?: TypeStep;
  startAtSec?: number;
  color?: string;
  align?: "left" | "center" | "right";
  idSuffix?: string;
  maxWidthPx?: number;
}): { html: string; init: string; tween: string; id: string } {
  const id = `${opts.clipPrefix}-${opts.idSuffix ?? "body"}`;
  const size = typeSize(opts.step ?? "md", opts.format);
  const align = opts.align ?? "center";
  const startAt = opts.startAtSec ?? 0.2;
  const t = makeTl(opts.clipStartSec);
  const color = opts.color ?? withAlpha(colors.fg, 0.85);
  const maxW = opts.maxWidthPx ? `max-width:${opts.maxWidthPx}px;` : "";
  const html = `<div id="${id}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${size}px;letter-spacing:-0.4px;line-height:1.3;color:${color};text-align:${align};${maxW}">${escapeHtml(opts.text)}</div>`;
  return {
    html,
    id,
    init: `gsap.set("#${id}", { opacity: 0, y: 12 });`,
    tween: t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.47, ease: "raw:\"power1.out\"" }, startAt),
  };
}

/**
 * Emit a `<img>` for the brand logo (or a circle fallback when the brand
 * logo is missing / non-image). Common to all logo-intro/outro scenes.
 */
export function brandLogoImg(opts: { id: string; sizePx: number; color?: string; tintWhite?: boolean }): string {
  const src = resolveBrandLogoSrc();
  if (!src) {
    return `<div id="${opts.id}" style="width:${opts.sizePx}px;height:${opts.sizePx}px;border-radius:9999px;background:${opts.color ?? colors.fg};"></div>`;
  }
  // Im Light-Theme das Logo dunkel statt weiß tinten (sonst unsichtbar auf hell).
  const tint = opts.tintWhite
    ? activeColorTheme === "light"
      ? "filter:brightness(0);"
      : "filter:brightness(0) invert(1);"
    : "";
  return `<img id="${opts.id}" src="${escapeHtml(src)}" alt="${escapeHtml(brandName)}" style="height:${opts.sizePx}px;width:auto;object-fit:contain;${tint}" />`;
}

/** Inline style string for an absolutely-positioned fill layer. */
export function fillStyle(extra = ""): string {
  return `position:absolute;inset:0;${extra}`;
}

/**
 * Wandelt einen SVG-String in eine sichere CSS-`url(...)`-Daten-URL um.
 *
 * Hintergrund: `<div style="background-image:url(\"data:image/svg+xml;utf8,<svg …>…</svg>\")">`
 * bricht den HTML-Parser, weil das innere `"` das `style`-Attribut schließt.
 * Folge: das SVG samt Reststyle (`opacity:0.09;mix-blend-mode:overlay;`) landet
 * als sichtbarer Text im Dokument.
 *
 * Lösung: SVG vollständig URL-enkodieren und in einfache Quotes packen.
 */
export function svgDataUrl(svg: string): string {
  const encoded = encodeURIComponent(svg.trim()).replace(/'/g, "%27");
  return `url('data:image/svg+xml;utf8,${encoded}')`;
}

/**
 * Sets `gsap.set(target, props)` as an init line (string).
 */
export function gsapSetLine(target: string, props: Record<string, unknown>): string {
  return `gsap.set(${JSON.stringify(target)}, ${propsLiteral(props)});`;
}

/**
 * Stringify a GSAP props object so that string values starting with `raw:`
 * become unquoted JS expressions (e.g. `raw:"power2.out"` → `"power2.out"`).
 */
export function propsLiteral(o: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(o)) {
    if (typeof v === "string" && v.startsWith("raw:")) {
      parts.push(`${JSON.stringify(k)}:${v.slice(4)}`);
    } else if (v === undefined) {
      // skip
    } else {
      parts.push(`${JSON.stringify(k)}:${JSON.stringify(v)}`);
    }
  }
  return `{${parts.join(",")}}`;
}

/**
 * Common Fragment merge for a scene builder — combines the HTML, css, init
 * and tweens emitted across its sub-helpers.
 */
export function combineSceneFragments(parts: Fragment[]): Fragment {
  return {
    html: parts.map((p) => p.html).join("\n"),
    css: parts
      .map((p) => p.css)
      .filter((c): c is string => Boolean(c))
      .join("\n"),
    init: parts
      .map((p) => p.init)
      .filter((s): s is string => Boolean(s))
      .join("\n"),
    tweens: parts.flatMap((p) => p.tweens ?? []),
  };
}
